import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, UtensilsCrossed, ChevronDown } from 'lucide-react';
import { menuData } from '@/data/index';
import { RESTAURANT_CONSTANTS } from '@/lib/index';
import { MenuCategorySection } from '@/components/MenuCards';
import { IMAGES } from '@/assets/images';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

const Menu = () => {
  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="min-h-screen bg-background"
    >
      {/* Hero Section - Menu Page */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.AFRICAN_CUISINE_4}
            alt="Cuisine Artisanale"
            className="w-full h-full object-cover opacity-40 scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/40 to-background" />
        </div>
        
        <div className="relative z-10 text-center px-4 max-w-4xl">
          <motion.div variants={fadeInUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary border border-primary/20 mb-6 backdrop-blur-sm">
              <UtensilsCrossed className="w-4 h-4" />
              <span className="text-sm font-semibold uppercase tracking-wider">Notre Carte</span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6">
              L'Art de Bien <span className="text-primary">Manger</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Plongez dans un voyage gustatif où les saveurs de l'Afrique rencontrent le raffinement européen.
            </p>
          </motion.div>
          
          <motion.div 
            variants={fadeInUp}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce"
          >
            <ChevronDown className="w-6 h-6 text-muted-foreground" />
          </motion.div>
        </div>
      </section>

      {/* Category Navigation (Quick Access) */}
      <nav className="sticky top-[64px] z-40 bg-background/80 backdrop-blur-md border-b border-border py-4">
        <div className="container mx-auto px-4 overflow-x-auto no-scrollbar">
          <div className="flex items-center justify-center min-w-max gap-8 md:gap-12">
            {menuData.map((category) => (
              <a 
                key={category.id} 
                href={`#${category.id}`}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
              >
                {category.title}
              </a>
            ))}
          </div>
        </div>
      </nav>

      {/* Galerie de nos plats */}
      <section className="py-16 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4">Nos Créations en Images</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Chaque plat raconte une histoire, découvrez la beauté de nos créations culinaires
            </p>
          </div>
          
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
          >
            {[
              { image: IMAGES.CAPTURE_D_CRAN2854, alt: "Plat africain traditionnel" },
              { image: IMAGES.CAPTURE_D_CRAN6495, alt: "Spécialité du chef" },
              { image: IMAGES.CAPTURE_D_CRAN8415, alt: "Cuisine européenne raffinée" },
              { image: IMAGES.CAPTURE_D_CRAN2274, alt: "Fusion africaine-européenne" },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ scale: 1.05 }}
                className="aspect-square rounded-xl overflow-hidden shadow-md cursor-pointer group"
              >
                <img 
                  src={item.image} 
                  alt={item.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Menu Sections */}
      <section className="py-12 md:py-24 container mx-auto px-4">
        <div className="space-y-32">
          {menuData.map((category) => (
            <div key={category.id} id={category.id} className="scroll-mt-32">
              <MenuCategorySection category={category} />
            </div>
          ))}
        </div>
      </section>

      {/* Sticky WhatsApp CTA for Mobile */}
      <div className="fixed bottom-6 right-6 z-50 md:hidden">
        <a 
          href={RESTAURANT_CONSTANTS.WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center w-14 h-14 bg-accent text-accent-foreground rounded-full shadow-2xl shadow-accent/40 active:scale-90 transition-transform"
        >
          <MessageCircle className="w-7 h-7" />
        </a>
      </div>

      {/* Footer CTA Section */}
      <section className="py-24 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src={IMAGES.AFRICAN_CUISINE_3}
            className="w-full h-full object-cover"
            alt="Decorative background"
          />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-8">Vous avez fait votre choix ?</h2>
            <p className="text-xl opacity-90 mb-12 leading-relaxed">
              Commandez directement sur WhatsApp. Nous préparons votre plat à la minute avec des produits frais.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a 
                href={RESTAURANT_CONSTANTS.WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 bg-accent text-accent-foreground px-10 py-5 rounded-full text-xl font-bold hover:bg-accent/90 hover:scale-105 active:scale-95 transition-all shadow-xl shadow-accent/30"
              >
                <MessageCircle className="w-6 h-6" />
                Commander maintenant
              </a>
            </div>
            <p className="mt-8 text-sm opacity-70 italic font-mono">
              {RESTAURANT_CONSTANTS.HOURS}
            </p>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Menu;